import { LIVE_PLANT_ZONE_LIMITS, type EdiblePlantSpeciesId, type PlantTestSeed } from "./ecosystem-zones.js";

export type StormRegrowthStage = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface StormRegrowthScheduleStep {
  stage: Exclude<StormRegrowthStage, 0>;
  dueAt: Date;
  minimumStormProgress: number;
  seedIds: string[];
}

export interface StormRegrowthChoreography {
  environmentId: typeof pitStormRegrowthCorridor.environmentId;
  serverName: typeof pitStormRegrowthCorridor.serverName;
  corridorRevision: typeof pitStormRegrowthCorridor.revision;
  routeId: typeof pitStormRegrowthCorridor.routeId;
  stormStartedAt: Date;
  baselinePlanFingerprint: string;
  baselinePlan: PlantTestSeed[];
  schedule: StormRegrowthScheduleStep[];
  commandAuthority: "none_source_only";
}

export interface StormRegrowthStageIntent {
  environmentId: typeof pitStormRegrowthCorridor.environmentId;
  corridorRevision: typeof pitStormRegrowthCorridor.revision;
  routeId: typeof pitStormRegrowthCorridor.routeId;
  fromStage: StormRegrowthStage;
  toStage: Exclude<StormRegrowthStage, 0>;
  observedStormProgress: number;
  progressReadbackReference: string;
  addedSeedIds: string[];
  desiredPlan: PlantTestSeed[];
  replenishment: "disabled";
  executable: false;
}

export interface StormRegrowthRetirementIntent {
  environmentId: typeof pitStormRegrowthCorridor.environmentId;
  corridorRevision: typeof pitStormRegrowthCorridor.revision;
  fromStage: StormRegrowthStage;
  exactOwnedSeedIds: string[];
  desiredBaselinePlan: PlantTestSeed[];
  terminalStormReference: string;
  executable: false;
}

export interface StormRegrowthReadinessProof {
  environmentId: typeof pitStormRegrowthCorridor.environmentId;
  corridorRevision: typeof pitStormRegrowthCorridor.revision;
  stormRouteAcceptanceReference: string;
  plantLifecycleAcceptanceReference: string;
  baselinePlanFingerprint: string;
  exactRetirementReference: string;
  clientObservationReferences: [string, string];
  observedAt: Date;
}

const species: readonly EdiblePlantSpeciesId[] = ["fireweed", "marigold", "trillium", "red_currant"];
// Keep the reviewed Pit route local to this bounded compiler. The wider
// website-forecast contract remains on its own release path and is not a
// prerequisite for safely compiling or retiring Aftergrowth seed plans.
const route = {
  id: "owner_pit_eastbound",
  points: [
    { worldX: -367_931.157, worldY: 279_516.525 },
    { worldX: -355_931.157, worldY: 279_516.525 }
  ],
  movementSeconds: 90
} as const;

/**
 * Source-only event composition. The storm and every plant stage retain
 * separate authority; this compiler cannot write a PlantSpawner plan or arm an
 * environment effect.
 */
export const pitStormRegrowthCorridor = {
  revision: "pit-aftergrowth-r1",
  title: "Aftergrowth Trail",
  environmentId: "4c60a401",
  serverName: "The Nest Development",
  routeId: route.id,
  zoneId: "living-world-pit-regrowth",
  seedPrefix: "pit-regrowth-",
  stageCount: 6,
  seedsPerStage: 4,
  maximumSeeds: 24,
  stageIntervalSeconds: 15,
  trailDelaySeconds: 15,
  stormMovementSeconds: route.movementSeconds,
  corridorHalfWidthWorldUnits: 225,
  minimumBaselineCapacity: {
    maximumExistingZones: LIVE_PLANT_ZONE_LIMITS.maxZones - 1,
    maximumExistingSeeds: LIVE_PLANT_ZONE_LIMITS.maxTotal - 24
  },
  cleanup: "remove exact corridor seed IDs through PlantSpawner plan retirement",
  replenishment: "disabled for temporary corridor",
  status: "source_only_acceptance_required"
} as const;

export function compilePitStormRegrowthStage(stage: StormRegrowthStage): PlantTestSeed[] {
  if (!Number.isInteger(stage) || stage < 0 || stage > pitStormRegrowthCorridor.stageCount) {
    throw new Error("Storm regrowth stage must be a whole number from 0 to 6.");
  }
  const count = stage * pitStormRegrowthCorridor.seedsPerStage;
  const start = route.points[0];
  const finish = route.points[1];
  return Array.from({ length: count }, (_, index) => {
    const progress = (index + 0.5) / pitStormRegrowthCorridor.maximumSeeds;
    const crossTrack = [120, -120, 220, -220][index % 4]!;
    return {
      id: `${pitStormRegrowthCorridor.seedPrefix}${String(index + 1).padStart(2, "0")}`,
      zoneId: pitStormRegrowthCorridor.zoneId,
      species: species[index % species.length]!,
      targetX: round(start.worldX + ((finish.worldX - start.worldX) * progress)),
      targetY: round(start.worldY + ((finish.worldY - start.worldY) * progress) + crossTrack)
    };
  });
}

export function mergePitStormRegrowthStage(baseline: PlantTestSeed[], stage: StormRegrowthStage): PlantTestSeed[] {
  const cleanBaseline = baseline.filter((seed) => seed.zoneId !== pitStormRegrowthCorridor.zoneId && !seed.id.startsWith(pitStormRegrowthCorridor.seedPrefix));
  if (cleanBaseline.length !== baseline.length) throw new Error("Storm regrowth baseline already contains corridor-owned seeds.");
  const zones = new Set(cleanBaseline.map((seed) => seed.zoneId));
  if (zones.size > pitStormRegrowthCorridor.minimumBaselineCapacity.maximumExistingZones) {
    throw new Error("Storm regrowth requires one free PlantSpawner zone slot.");
  }
  const additions = compilePitStormRegrowthStage(stage);
  if (cleanBaseline.length + additions.length > LIVE_PLANT_ZONE_LIMITS.maxTotal) {
    throw new Error("Storm regrowth would exceed the live plant ceiling.");
  }
  const ids = [...cleanBaseline, ...additions].map((seed) => seed.id);
  if (new Set(ids).size !== ids.length) throw new Error("Storm regrowth plan contains duplicate seed identities.");
  return [...cleanBaseline, ...additions];
}

export function removePitStormRegrowthSeeds(plan: PlantTestSeed[]): PlantTestSeed[] {
  return plan.filter((seed) => seed.zoneId !== pitStormRegrowthCorridor.zoneId && !seed.id.startsWith(pitStormRegrowthCorridor.seedPrefix));
}

export function validateStormRegrowthReadiness(proof: StormRegrowthReadinessProof, now: Date): StormRegrowthReadinessProof {
  if (proof.environmentId !== pitStormRegrowthCorridor.environmentId || proof.corridorRevision !== pitStormRegrowthCorridor.revision) {
    throw new Error("Storm regrowth evidence is not pinned to the reviewed Edge corridor.");
  }
  const observedAt = new Date(proof.observedAt);
  finite(observedAt.getTime(), "observation time");
  finite(now.getTime(), "validation time");
  const age = now.getTime() - observedAt.getTime();
  if (age < -5_000 || age > 60_000) throw new Error("Storm regrowth evidence must be fresh within 60 seconds.");
  const clients = proof.clientObservationReferences.map((value) => evidence(value, "client observation")) as [string, string];
  if (clients[0] === clients[1]) throw new Error("Storm regrowth requires two distinct client observations.");
  return {
    ...proof,
    observedAt,
    clientObservationReferences: clients,
    stormRouteAcceptanceReference: evidence(proof.stormRouteAcceptanceReference, "storm-route acceptance"),
    plantLifecycleAcceptanceReference: evidence(proof.plantLifecycleAcceptanceReference, "plant-lifecycle acceptance"),
    baselinePlanFingerprint: evidence(proof.baselinePlanFingerprint, "baseline plan fingerprint"),
    exactRetirementReference: evidence(proof.exactRetirementReference, "exact retirement")
  };
}

/**
 * Compiles the exact spatial/temporal trail. Each four-plant stage becomes due
 * only after the 90-second Stormfront has passed the furthest seed in that
 * stage, plus the reviewed 15-second ecological delay. This remains pure data.
 */
export function compilePitStormRegrowthChoreography(input: {
  baselinePlan: PlantTestSeed[];
  readiness: StormRegrowthReadinessProof;
  stormStartedAt: Date;
  now: Date;
}): StormRegrowthChoreography {
  const readiness = validateStormRegrowthReadiness(input.readiness, input.now);
  finite(input.stormStartedAt.getTime(), "storm start time");
  const startAge = input.now.getTime() - input.stormStartedAt.getTime();
  if (startAge < -5_000 || startAge > 60_000) throw new Error("Storm regrowth choreography must be compiled at the fresh Stormfront start.");
  const baselinePlan = input.baselinePlan.map((seed) => ({ ...seed }));
  mergePitStormRegrowthStage(baselinePlan, 6);
  const start = route.points[0];
  const finish = route.points[1];
  const routeDeltaX = finish.worldX - start.worldX;
  if (!Number.isFinite(routeDeltaX) || routeDeltaX === 0) throw new Error("Storm regrowth route progress cannot be resolved.");
  const schedule = Array.from({ length: pitStormRegrowthCorridor.stageCount }, (_, index) => {
    const stage = (index + 1) as Exclude<StormRegrowthStage, 0>;
    const seeds = compilePitStormRegrowthStage(stage);
    const previousCount = index * pitStormRegrowthCorridor.seedsPerStage;
    const stageSeeds = seeds.slice(previousCount);
    const minimumStormProgress = Math.max(...stageSeeds.map((seed) => (seed.targetX - start.worldX) / routeDeltaX));
    const dueMilliseconds = Math.ceil((minimumStormProgress * pitStormRegrowthCorridor.stormMovementSeconds
      + pitStormRegrowthCorridor.trailDelaySeconds) * 1_000);
    return {
      stage,
      dueAt: new Date(input.stormStartedAt.getTime() + dueMilliseconds),
      minimumStormProgress,
      seedIds: stageSeeds.map((seed) => seed.id)
    };
  });
  return {
    environmentId: pitStormRegrowthCorridor.environmentId,
    serverName: pitStormRegrowthCorridor.serverName,
    corridorRevision: pitStormRegrowthCorridor.revision,
    routeId: pitStormRegrowthCorridor.routeId,
    stormStartedAt: input.stormStartedAt,
    baselinePlanFingerprint: readiness.baselinePlanFingerprint,
    baselinePlan,
    schedule,
    commandAuthority: "none_source_only"
  };
}

export function compilePitStormRegrowthAdvance(choreography: StormRegrowthChoreography, input: {
  currentStage: StormRegrowthStage;
  nextStage: Exclude<StormRegrowthStage, 0>;
  currentPlan: PlantTestSeed[];
  observedStormProgress: number;
  progressReadbackReference: string;
  now: Date;
}): StormRegrowthStageIntent {
  requireChoreography(choreography);
  if (input.nextStage !== input.currentStage + 1) throw new Error("Storm regrowth stages must advance exactly once and in order.");
  const step = choreography.schedule.find((candidate) => candidate.stage === input.nextStage);
  if (!step) throw new Error("Storm regrowth next stage is outside the reviewed choreography.");
  finite(input.now.getTime(), "advance time");
  if (input.now.getTime() < step.dueAt.getTime()) throw new Error("Storm regrowth stage is not due behind the moving front yet.");
  if (!Number.isFinite(input.observedStormProgress) || input.observedStormProgress < 0 || input.observedStormProgress > 1) {
    throw new Error("Storm regrowth observed progress must be between zero and one.");
  }
  if (input.observedStormProgress + Number.EPSILON < step.minimumStormProgress) {
    throw new Error("Storm regrowth cannot appear before the Stormfront has passed this route section.");
  }
  const expectedCurrent = mergePitStormRegrowthStage(choreography.baselinePlan, input.currentStage);
  if (!samePlan(input.currentPlan, expectedCurrent)) throw new Error("Storm regrowth current plan does not match the exact baseline and owned stage.");
  const desiredPlan = mergePitStormRegrowthStage(choreography.baselinePlan, input.nextStage);
  return {
    environmentId: pitStormRegrowthCorridor.environmentId,
    corridorRevision: pitStormRegrowthCorridor.revision,
    routeId: pitStormRegrowthCorridor.routeId,
    fromStage: input.currentStage,
    toStage: input.nextStage,
    observedStormProgress: input.observedStormProgress,
    progressReadbackReference: evidence(input.progressReadbackReference, "storm progress read-back"),
    addedSeedIds: step.seedIds,
    desiredPlan,
    replenishment: "disabled",
    executable: false
  };
}

export function compilePitStormRegrowthRetirement(choreography: StormRegrowthChoreography, input: {
  currentStage: StormRegrowthStage;
  currentPlan: PlantTestSeed[];
  terminalStormReference: string;
}): StormRegrowthRetirementIntent {
  requireChoreography(choreography);
  const expectedCurrent = mergePitStormRegrowthStage(choreography.baselinePlan, input.currentStage);
  if (!samePlan(input.currentPlan, expectedCurrent)) throw new Error("Storm regrowth retirement refuses a changed baseline or owned stage.");
  const exactOwnedSeedIds = compilePitStormRegrowthStage(input.currentStage).map((seed) => seed.id);
  const desiredBaselinePlan = removePitStormRegrowthSeeds(input.currentPlan);
  if (!samePlan(desiredBaselinePlan, choreography.baselinePlan)) throw new Error("Storm regrowth retirement did not recover the exact baseline plan.");
  return {
    environmentId: pitStormRegrowthCorridor.environmentId,
    corridorRevision: pitStormRegrowthCorridor.revision,
    fromStage: input.currentStage,
    exactOwnedSeedIds,
    desiredBaselinePlan,
    terminalStormReference: evidence(input.terminalStormReference, "terminal storm"),
    executable: false
  };
}

function requireChoreography(choreography: StormRegrowthChoreography): void {
  if (choreography.environmentId !== pitStormRegrowthCorridor.environmentId
    || choreography.serverName !== pitStormRegrowthCorridor.serverName
    || choreography.corridorRevision !== pitStormRegrowthCorridor.revision
    || choreography.routeId !== pitStormRegrowthCorridor.routeId
    || choreography.commandAuthority !== "none_source_only") {
    throw new Error("Storm regrowth choreography is not pinned to the reviewed Edge route and authority boundary.");
  }
}

function samePlan(left: PlantTestSeed[], right: PlantTestSeed[]): boolean {
  if (left.length !== right.length) return false;
  const canonical = (seed: PlantTestSeed) => `${seed.id}|${seed.zoneId}|${seed.species}|${seed.targetX}|${seed.targetY}`;
  const leftCanonical = left.map(canonical).sort();
  const rightCanonical = right.map(canonical).sort();
  return leftCanonical.every((value, index) => value === rightCanonical[index]);
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

function finite(value: number, label: string): void {
  if (!Number.isFinite(value)) throw new Error(`Storm regrowth ${label} must be finite.`);
}

function evidence(value: string, label: string): string {
  const normalized = value.trim();
  if (normalized.length < 8 || normalized.length > 200) throw new Error(`Storm regrowth ${label} evidence must be 8 to 200 characters.`);
  return normalized;
}
